import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    getHomeBrowsePuzzleTitle,
    getHomeStoryReadApiId,
    getLocalizedHomeBrowseStoryTeaser,
} from "../../browse-puzzles/homePuzzleRoUtils";
import { usePagesTranslation } from "../../locales";

/** Distance moved before we treat interaction as drag (suppress card navigation). */
const DRAG_THRESHOLD_PX = 8;

/** Gap between cards — keep in sync with `gap` on the flex strip below (theme spacing * 8px). */
const STRIP_GAP_PX = 16;

/** Identical copies of the API list (≥3). More segments = safer jumps before the scroll hits a hard edge. */
const LOOP_SEGMENTS = 7;

/** Start scrolled so the viewport sits on a middle segment (left/right room before wrap). */
const MID_SEGMENT_INDEX = Math.floor(LOOP_SEGMENTS / 2);

/** Book-style cover proportions (aligned with storybook shelf). */
const LANDING_BOOK_WIDTH = { xs: 128, sm: 140, md: 148 };
const LANDING_BOOK_HEIGHT = { xs: 192, sm: 210, md: 222 };

/** Slide transition — matches TypeOfPuzzlesCarousel transform duration (0.65s). */
const SCROLL_ANIM_MS = 650;

function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function LandingStoryCarousel({ items, contentLanguage }) {
    const { t } = usePagesTranslation();
    const viewportRef = useRef(null);
    const lanePxRef = useRef(0);
    const draggingRef = useRef(false);
    const isAnimatingRef = useRef(false);
    const scrollAnimFrameRef = useRef(0);
    const dragOriginRef = useRef({ clientX: 0, scrollLeft: 0 });
    const suppressNextClickRef = useRef(false);

    /** Same API order repeated many times so swipe/drag can loop without hitting the scroll ends. */
    const loopStripItems = useMemo(() => {
        if (items.length === 0) return [];
        return Array.from({ length: LOOP_SEGMENTS }, () => items).flat();
    }, [items]);

    const measureLane = useCallback(() => {
        const el = viewportRef.current;
        if (!el || items.length === 0) return 0;
        const total = el.scrollWidth;
        const lane = total / LOOP_SEGMENTS;
        lanePxRef.current = lane;
        return lane;
    }, [items.length]);

    const normalizeInfiniteScroll = useCallback(() => {
        if (draggingRef.current || isAnimatingRef.current) return;
        const el = viewportRef.current;
        const lane = lanePxRef.current;
        if (!el || lane <= 0) return;
        const epsilon = Math.min(56, Math.max(12, lane * 0.02));
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        if (maxScroll <= 0) return;
        if (el.scrollLeft <= epsilon) {
            el.scrollLeft += lane;
        } else if (maxScroll - el.scrollLeft <= epsilon) {
            el.scrollLeft -= lane;
        }
    }, []);

    useLayoutEffect(() => {
        const el = viewportRef.current;
        if (!el || items.length === 0) return;
        const lane = measureLane();
        if (lane > 0) {
            el.scrollLeft = lane * MID_SEGMENT_INDEX;
        }
    }, [items, contentLanguage, measureLane]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const onScroll = () => normalizeInfiniteScroll();
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [normalizeInfiniteScroll]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => {
            const prevLane = lanePxRef.current;
            measureLane();
            const lane = lanePxRef.current;
            if (!lane || prevLane <= 0 || Math.abs(prevLane - lane) < 2) return;
            /** Strip width changed (e.g. images) — re-center on middle segment. */
            el.scrollLeft = lane * MID_SEGMENT_INDEX;
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [measureLane]);

    const onPointerDown = useCallback((e) => {
        if (e.pointerType !== "mouse") return;
        if (e.button !== 0) return;
        draggingRef.current = true;
        suppressNextClickRef.current = false;
        const el = viewportRef.current;
        if (!el) return;
        dragOriginRef.current = {
            clientX: e.clientX,
            scrollLeft: el.scrollLeft,
        };
        try {
            el.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        el.style.cursor = "grabbing";
    }, []);

    const onPointerMove = useCallback((e) => {
        if (e.pointerType !== "mouse" || !draggingRef.current) return;
        const el = viewportRef.current;
        if (!el) return;
        const dx = e.clientX - dragOriginRef.current.clientX;
        if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
            suppressNextClickRef.current = true;
        }
        el.scrollLeft = dragOriginRef.current.scrollLeft - dx;
    }, []);

    const endDrag = useCallback(
        (e) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            const el = viewportRef.current;
            if (el) {
                el.style.cursor = "grab";
                try {
                    if (e?.pointerId != null) el.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
            }
            window.setTimeout(() => {
                suppressNextClickRef.current = false;
            }, 0);
            requestAnimationFrame(() => {
                normalizeInfiniteScroll();
            });
        },
        [normalizeInfiniteScroll]
    );

    const getScrollStepPx = useCallback(() => {
        const el = viewportRef.current;
        const card = el?.querySelector("[data-carousel-strip-card]");
        if (!card) return 280 + STRIP_GAP_PX;
        return card.getBoundingClientRect().width + STRIP_GAP_PX;
    }, []);

    const scrollCarouselByDirection = useCallback(
        (dir) => {
            const el = viewportRef.current;
            if (!el || isAnimatingRef.current) return;

            const delta = dir * getScrollStepPx();
            const startLeft = el.scrollLeft;
            const startTime = performance.now();

            if (scrollAnimFrameRef.current) {
                cancelAnimationFrame(scrollAnimFrameRef.current);
            }
            isAnimatingRef.current = true;

            const tick = (now) => {
                const progress = Math.min(1, (now - startTime) / SCROLL_ANIM_MS);
                el.scrollLeft = startLeft + delta * easeInOutCubic(progress);
                if (progress < 1) {
                    scrollAnimFrameRef.current = requestAnimationFrame(tick);
                    return;
                }
                scrollAnimFrameRef.current = 0;
                isAnimatingRef.current = false;
                normalizeInfiniteScroll();
            };

            scrollAnimFrameRef.current = requestAnimationFrame(tick);
        },
        [getScrollStepPx, normalizeInfiniteScroll]
    );

    const carouselArrowIconButtonSx = {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        opacity: 0.5,
        width: 44,
        height: 44,
        padding: 0,
        color: "rgba(27, 56, 40, 0.92)",
        bgcolor: "rgba(255, 255, 255, 0.92)",
        border: "1px solid rgba(59, 166, 102, 0.28)",
        boxShadow: "0 2px 10px rgba(45, 52, 54, 0.12)",
        borderRadius: "50%",
        "&:hover": {
            opacity: 0.5,
            bgcolor: "rgba(255, 255, 255, 0.96)",
        },
    };

    if (!items.length) return null;

    return (
        <Box
            sx={{
                width: "100%",
                boxSizing: "border-box",
                mb: { xs: 2.5, sm: 4 },
                pb: 0.5,
                position: "relative",
            }}
        >
            <IconButton
                type="button"
                aria-label={t("landing.carousel-prev")}
                onClick={(e) => {
                    e.stopPropagation();
                    scrollCarouselByDirection(-1);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                sx={{
                    ...carouselArrowIconButtonSx,
                    left: { xs: 8, sm: 12 },
                }}
            >
                <ChevronLeftIcon sx={{ fontSize: 28 }} aria-hidden />
            </IconButton>
            <IconButton
                type="button"
                aria-label={t("landing.carousel-next")}
                onClick={(e) => {
                    e.stopPropagation();
                    scrollCarouselByDirection(1);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                sx={{
                    ...carouselArrowIconButtonSx,
                    right: { xs: 8, sm: 12 },
                }}
            >
                <ChevronRightIcon sx={{ fontSize: 28 }} aria-hidden />
            </IconButton>
            <Box
                ref={viewportRef}
                role="region"
                aria-label={t("landing.carousel-region-label")}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={(e) => {
                    if (draggingRef.current) endDrag(e);
                }}
                onLostPointerCapture={() => {
                    draggingRef.current = false;
                    const el = viewportRef.current;
                    if (el) el.style.cursor = "grab";
                    requestAnimationFrame(() => {
                        normalizeInfiniteScroll();
                    });
                }}
                sx={{
                    display: "flex",
                    gap: `${STRIP_GAP_PX}px`,
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 1,
                    cursor: "grab",
                    touchAction: "pan-x",
                    userSelect: "none",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {loopStripItems.map((item, idx) => {
                    const { storyTitle, imageUrl } = getLocalizedHomeBrowseStoryTeaser(
                        item,
                        contentLanguage
                    );
                    const title =
                        (storyTitle && storyTitle.trim()) || getHomeBrowsePuzzleTitle(item);
                    const readId = getHomeStoryReadApiId(item);
                    const baseKey =
                        readId && readId.length > 0
                            ? readId
                            : `carousel-${idx % Math.max(items.length, 1)}`;
                    const loopKey = `${baseKey}-strip-${idx}`;
                    const canOpen = Boolean(readId);

                    const storyPath = readId
                        ? `/read-story/${encodeURIComponent(readId)}`
                        : undefined;

                    return (
                        <Box
                            key={loopKey}
                            data-carousel-strip-card
                            sx={{
                                flex: "0 0 auto",
                                width: LANDING_BOOK_WIDTH,
                                display: "block",
                                borderRadius: "6px 10px 10px 6px",
                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 8px 20px rgba(59, 166, 102, 0.28)",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: LANDING_BOOK_HEIGHT,
                                    borderRadius: "4px 8px 8px 4px",
                                    overflow: "hidden",
                                    bgcolor: "#5c4033",
                                    border: "1px solid",
                                    borderColor: "rgba(0,0,0,0.2)",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {canOpen && storyPath ? (
                                    <Tooltip title={t("landing.carousel-read-aria")} arrow>
                                        <IconButton
                                            component={RouterLink}
                                            to={storyPath}
                                            size="small"
                                            aria-label={t("landing.carousel-read-aria")}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                position: "absolute",
                                                top: 6,
                                                right: 6,
                                                zIndex: 2,
                                                opacity: 0.6,
                                                color: "#fff",
                                                bgcolor: "rgba(0, 0, 0, 0.35)",
                                                pointerEvents: "auto",
                                                "&:hover": {
                                                    opacity: 1,
                                                    bgcolor: "rgba(0, 0, 0, 0.5)",
                                                },
                                            }}
                                        >
                                            <MenuBookIcon fontSize="small" aria-hidden />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                {imageUrl ? (
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt=""
                                        draggable={false}
                                        sx={{
                                            width: "100%",
                                            flex: 1,
                                            minHeight: 0,
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background:
                                                "linear-gradient(145deg, #e8f6ee 0%, #f2faf5 50%, #dcefe4 100%)",
                                            color: "rgba(44, 62, 45, 0.45)",
                                        }}
                                    >
                                        <MenuBookIcon sx={{ fontSize: 40 }} aria-hidden />
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        px: 0.75,
                                        py: 0.5,
                                        bgcolor: "rgba(44, 62, 45, 0.92)",
                                        borderTop: "1px solid rgba(255,255,255,0.12)",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        sx={{
                                            color: "#fff",
                                            fontWeight: 600,
                                            lineHeight: 1.25,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            wordBreak: "break-word",
                                        }}
                                        title={title}
                                    >
                                        {title}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default LandingStoryCarousel;
